import { useState } from "react";
import { PlayCircle, Sparkles, Tag, TrendingUp, Search, Cpu, Target } from "lucide-react";
import { useTheme } from "../context/ThemeContext";
import NucleusIntroModal from "../shared/NucleusIntroModal";

const NUCLEUS_LOGO = "https://customer-assets.emergentagent.com/job_24108f3e-b858-4890-993f-f1e1a60b89e6/artifacts/2rd3rfv1_NucluesOS_horizontal-15_nomargins.png";
const DARE_LOGO = "/assets/DARE.png";

const blocks = [
  {
    id: "nucleus",
    label: "NucleusOS",
    tagline: "Smarter catalogues. AI-driven.",
    logo: NUCLEUS_LOGO,
    logoHeight: 56,
    features: [
      { icon: Sparkles, text: "AI-powered content recommendations" },
      { icon: Tag, text: "Automated metadata tagging" },
      { icon: TrendingUp, text: "Predictive content performance" },
    ],
    outcomes: ["Higher passenger satisfaction", "Optimised licensing ROI"],
  },
  {
    id: "dare",
    label: "DARE",
    tagline: "Personal to every passenger.",
    logo: DARE_LOGO,
    logoHeight: 72,
    features: [
      { icon: Search, text: "Real-time preference learning" },
      { icon: Cpu, text: "Dynamic UX adaptation" },
      { icon: Target, text: "Bespoke interface personalisation" },
    ],
    outcomes: ["Improved relevance", "Maximised engagement"],
  },
];

const SlideIntelligent = () => {
  const { isDark } = useTheme();
  const [showIntro, setShowIntro] = useState(false);
  const [activeId, setActiveId] = useState(null);

  return (
    <div>
      <div className="flex items-start justify-between gap-6">
        <div>
          <div className="k-eyebrow" style={{ color: "#3B82F6" }}>Intelligent — Engagement</div>
          <h2 className={`k-h2 mt-3 max-w-[15ch] ${isDark ? "text-white" : "text-[#1d1d1f]"}`}>
            AI that curates,
            <br />
            <span style={{ color: "#3B82F6" }}>learns and adapts.</span>
          </h2>
        </div>
        <button
          type="button"
          onClick={() => setShowIntro(true)}
          data-testid="nucleus-intro-btn"
          className={`hidden lg:inline-flex items-center gap-1.5 h-9 px-4 rounded-full text-[13px] font-semibold flex-shrink-0 ${
            isDark
              ? "bg-white/[0.06] text-white border border-white/15 hover:bg-white/[0.10]"
              : "bg-black/[0.04] text-[#1d1d1f] border border-black/15 hover:bg-black/[0.07]"
          }`}
        >
          <PlayCircle className="w-3.5 h-3.5" /> Watch 60s intro
        </button>
      </div>
      <p className={`k-lead mt-5 max-w-[52ch] ${isDark ? "text-white/70" : "text-black/60"}`}>
        Two products, one intelligence layer — NucleusOS behind the scenes,
        DARE in front of the passenger.
      </p>

      <div className="mt-8 grid grid-cols-2 gap-6">
        {blocks.map((b) => {
          const isOn = activeId === b.id;
          return (
            <button
              type="button"
              key={b.id}
              onClick={() => setActiveId(isOn ? null : b.id)}
              data-testid={`intelligent-block-${b.id}`}
              className={`text-left rounded-2xl p-6 border transition-all ${
                isDark
                  ? isOn ? "bg-white/[0.08]" : "bg-white/[0.03] border-white/10 hover:bg-white/[0.06]"
                  : isOn ? "bg-white" : "bg-white border-black/10 hover:border-black/25"
              }`}
              style={isOn ? { borderColor: "#3B82F6", boxShadow: `0 12px 40px -20px #3B82F6` } : undefined}
            >
              <div className="flex items-center justify-between gap-3 mb-6">
                <img src={b.logo} alt={b.label} style={{ height: b.logoHeight }} className="w-auto object-contain" />
              </div>
              <div className={`text-[22px] font-semibold tracking-tight ${isDark ? "text-white" : "text-[#1d1d1f]"}`}>
                {b.tagline}
              </div>
              <div className={`mt-5 space-y-3`}>
                {b.features.map((f, i) => {
                  const Icon = f.icon;
                  return (
                    <div key={i} className={`flex items-center gap-3 text-[14px] ${isDark ? "text-white/75" : "text-black/70"}`}>
                      <Icon className="w-4 h-4 flex-shrink-0" style={{ color: "#3B82F6" }} />
                      <span>{f.text}</span>
                    </div>
                  );
                })}
              </div>
              <div className={`mt-6 pt-4 border-t ${isDark ? "border-white/10" : "border-black/10"}`}>
                <div className="k-eyebrow" style={{ color: isDark ? "rgba(255,255,255,0.45)" : "rgba(0,0,0,0.45)" }}>Outcomes</div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {b.outcomes.map((o) => (
                    <span
                      key={o}
                      className="text-[12px] px-2.5 py-1 rounded-full font-medium"
                      style={{
                        backgroundColor: isDark ? "rgba(59,130,246,0.15)" : "rgba(59,130,246,0.12)",
                        color: isDark ? "#93C5FD" : "#1D4ED8",
                      }}
                    >
                      {o}
                    </span>
                  ))}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      <button
        type="button"
        onClick={() => setShowIntro(true)}
        data-testid="nucleus-intro-btn-mobile"
        className={`lg:hidden mt-6 inline-flex items-center gap-1.5 h-9 px-4 rounded-full text-[13px] font-semibold ${
          isDark ? "bg-white/[0.06] text-white border border-white/15" : "bg-black/[0.04] text-[#1d1d1f] border border-black/15"
        }`}
      >
        <PlayCircle className="w-3.5 h-3.5" /> Watch 60s intro
      </button>

      {showIntro && <NucleusIntroModal isDark={isDark} onClose={() => setShowIntro(false)} />}
    </div>
  );
};

export default SlideIntelligent;
